import { useRef, useState } from "react";
import { motion, useMotionValueEvent, useScroll, useTransform } from "framer-motion";
import { useLang } from "../i18n/LangContext";
import { MaskLines, Eyebrow, Reveal } from "../components/Reveal";

const Prism = ({ lit, i }) => (
  <div className="prism-wrap" aria-hidden>
    <div className={`prism ${lit ? "prism-lit" : ""}`} style={{ animationDuration: `${14 + i * 3}s` }}>
      {["f", "b", "l", "r", "t", "d"].map((f) => (
        <span key={f} className={`prism-face prism-${f}`} />
      ))}
    </div>
  </div>
);

export const Journey = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const [active, setActive] = useState(-1);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 70%", "end 75%"] });
  const scale = useTransform(scrollYProgress, [0, 1], [0, 1]);
  useMotionValueEvent(scrollYProgress, "change", (v) => setActive(Math.min(3, Math.floor(v * 4.2) - (v < 0.05 ? 1 : 0))));

  return (
    <section id="how" data-testid="journey-section" className="relative z-[2] overflow-hidden bg-noor-ink py-28 md:py-40">
      <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-noor-deep to-transparent" />
      <div className="relative mx-auto max-w-[1440px] px-6 lg:px-12">
        <div className="grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Eyebrow>{t.journey.eyebrow}</Eyebrow>
            <MaskLines lines={t.journey.lines} className="display-lg mt-6 font-display text-noor-pearl" lineClassName="italic-accent text-noor-champagne" testId="journey-headline" />
          </div>
        </div>
        <div ref={ref} className="relative mt-20 md:mt-28">
          <div className="absolute start-[27px] top-0 h-full w-px bg-noor-pearl/10 md:hidden">
            <motion.div style={{ scaleY: scale }} className="h-full w-px origin-top bg-noor-champagne" />
          </div>
          <div className="absolute inset-x-[28px] top-[28px] hidden h-px bg-noor-pearl/10 md:block">
            <motion.div style={{ scaleX: scale }} className="h-px origin-left bg-gradient-to-r from-noor-gold to-noor-champagne rtl:origin-right" />
          </div>
          <ol className="relative grid gap-14 md:grid-cols-4 md:gap-8">
            {t.journey.steps.map((s, i) => (
              <Reveal as="li" key={s.n} delay={i * 0.12} data-testid={`journey-step-${i + 1}`} className="flex gap-6 md:flex-col md:gap-0">
                <Prism lit={active >= i} i={i} />
                <div className="md:mt-10">
                  <span className={`font-brand text-sm transition-colors duration-700 ${active >= i ? "text-noor-champagne" : "text-noor-mist/70"}`}>{s.n}</span>
                  <h3 className="mt-3 font-display text-2xl leading-snug text-noor-pearl md:text-[1.7rem]">{s.title}</h3>
                  <p className="mt-4 max-w-xs text-[15px] leading-relaxed text-noor-mist">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};
